import { createPortal } from "react-dom";
import { createContext, useCallback, useContext, useEffect, useState } from "react";

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

export async function api<T = any>(path: string, opts: { method?: string; body?: unknown } = {}): Promise<T> {
  const init: RequestInit = { method: opts.method || "GET", headers: { "x-portal": "1" }, credentials: "same-origin" };
  if (opts.body instanceof FormData) init.body = opts.body;
  else if (opts.body !== undefined) {
    (init.headers as Record<string, string>)["content-type"] = "application/json";
    init.body = JSON.stringify(opts.body);
  }
  const res = await fetch(path, init);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401 || res.status === 428) window.dispatchEvent(new CustomEvent("portal:auth", { detail: res.status }));
    throw new ApiError(res.status, data.error || "Something went wrong.");
  }
  return data as T;
}

// Loads data and reloads when `deps` change. `reload` refetches.
export function useLoad<T>(path: string | null, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!path) return;
    let live = true;
    setError(null);
    api<T>(path)
      .then((d) => live && setData(d))
      .catch((e) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [path, tick, ...deps]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, reload, setData };
}

export type Me = {
  id: string;
  name: string;
  email: string;
  title: string;
  department: string | null;
  role_name: string;
  perms: string[];
  unread: number;
  must_change_password: boolean;
  has_2fa: boolean;
  open_decisions: number;
  phone_verified?: boolean;
  texts_ready?: boolean;
  needs_2fa: boolean;
  timezone: string;
  notify_prefs: Record<string, boolean>;
  has_photo: boolean;
  phone: string | null;
};
export const MeContext = createContext<{ me: Me; refreshMe: () => void; toast: (msg: string, bad?: boolean) => void }>(null as any);
export const useMe = () => useContext(MeContext);
export const can = (me: Me, p: string) => me.perms.includes(p);

// ---------- formatting (company time zone: America/Chicago) ----------
export const TZ = "America/Chicago";
export const todayYmd = () => new Intl.DateTimeFormat("en-CA", { timeZone: TZ }).format(new Date());
export function fmtDate(ymd: string | null | undefined) {
  if (!ymd) return "";
  const d = new Date(ymd.slice(0, 10) + "T12:00:00Z");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC", year: ymd.slice(0, 4) === todayYmd().slice(0, 4) ? undefined : "numeric" });
}
export function fmtDateTime(local: string | null | undefined) {
  // Scheduled times are stored as wall-clock America/Chicago ("2026-11-12T09:00").
  if (!local) return "";
  const [d, t] = local.split("T");
  if (!t) return fmtDate(d);
  const [h, m] = t.split(":").map(Number);
  const hh = ((h! + 11) % 12) + 1;
  return `${fmtDate(d)}, ${hh}:${String(m).padStart(2, "0")} ${h! < 12 ? "AM" : "PM"} CT`;
}
export function ago(iso: string) {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} hr ago`;
  if (s < 86400 * 7) return `${Math.round(s / 86400)} d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: TZ });
}
export const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? "" : "s"}`;

export const CONTENT_STATUS: Record<string, { label: string; tone: string }> = {
  draft: { label: "Draft", tone: "" },
  in_review: { label: "In review", tone: "info" },
  changes_requested: { label: "Changes requested", tone: "warn" },
  approved: { label: "Approved", tone: "good" },
  scheduled: { label: "Scheduled", tone: "accent" },
  published: { label: "Published", tone: "good" },
  publish_failed: { label: "Publish failed", tone: "bad" },
  rejected: { label: "Rejected", tone: "bad" },
  cancelled: { label: "Cancelled", tone: "" },
};
export const TASK_STATUS: Record<string, { label: string; tone: string }> = {
  todo: { label: "To do", tone: "" },
  in_progress: { label: "In progress", tone: "info" },
  blocked: { label: "Blocked", tone: "bad" },
  done: { label: "Done", tone: "good" },
  cancelled: { label: "Cancelled", tone: "" },
};
export const PLATFORMS = ["instagram", "facebook", "tiktok", "pinterest", "linkedin", "youtube", "threads", "x", "blog", "email"];
export const PLATFORM_LABEL: Record<string, string> = {
  instagram: "Instagram", facebook: "Facebook", tiktok: "TikTok", pinterest: "Pinterest", linkedin: "LinkedIn",
  youtube: "YouTube", threads: "Threads", x: "X", blog: "Blog", email: "Email",
};

export function Pill({ tone = "", children }: { tone?: string; children: React.ReactNode }) {
  return <span className={`pill ${tone}`}>{children}</span>;
}
export function StatusPill({ s, map }: { s: string; map: Record<string, { label: string; tone: string }> }) {
  const x = map[s] || { label: s, tone: "" };
  return <Pill tone={x.tone}>{x.label}</Pill>;
}
export const Sample = ({ on }: { on: any }) => (on ? <Pill tone="sample">Sample</Pill> : null);

export function Empty({ children }: { children: React.ReactNode }) {
  return <div className="empty">{children}</div>;
}
export function Loading({ error }: { error?: string | null }) {
  return error ? (
    <div className="banner bad">{error}</div>
  ) : (
    <div className="stack" style={{ gap: 10 }} aria-label="Loading">
      <div className="skeleton" style={{ height: 18, width: "40%" }} />
      <div className="skeleton" style={{ height: 64 }} />
      <div className="skeleton" style={{ height: 64, width: "85%" }} />
    </div>
  );
}

export function Drawer({ title, sub, onClose, children }: { title: React.ReactNode; sub?: React.ReactNode; onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  // Rendered at the page root, so a drawer opened from the sticky top bar isn't clipped to the bar.
  return createPortal(
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-modal="true">
        <div className="drawer-head">
          <div className="stack" style={{ gap: 6 }}>
            <h2>{title}</h2>
            {sub}
          </div>
          <button className="btn ghost sm" onClick={onClose} aria-label="Close">
            Close
          </button>
        </div>
        <div className="drawer-body">{children}</div>
      </aside>
    </>,
    document.body,
  );
}

// Hash routing: #/marketing?view=board
export function useRoute() {
  const parse = () => {
    const h = location.hash.replace(/^#/, "") || "/";
    const [path, qs] = h.split("?");
    return { path: path || "/", q: new URLSearchParams(qs || "") };
  };
  const [r, setR] = useState(parse);
  useEffect(() => {
    const f = () => setR(parse());
    window.addEventListener("hashchange", f);
    return () => window.removeEventListener("hashchange", f);
  }, []);
  return r;
}
export function go(path: string, q?: Record<string, string | null | undefined>) {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q || {})) if (v) p.set(k, v);
  const s = p.toString();
  location.hash = path + (s ? "?" + s : "");
}
export function setQuery(q: Record<string, string | null | undefined>) {
  const h = location.hash.replace(/^#/, "") || "/";
  const [path, qs] = h.split("?");
  const p = new URLSearchParams(qs || "");
  for (const [k, v] of Object.entries(q)) v ? p.set(k, v) : p.delete(k);
  const s = p.toString();
  history.replaceState(null, "", "#" + path + (s ? "?" + s : ""));
  window.dispatchEvent(new HashChangeEvent("hashchange"));
}

// A person's photo, or their initials when they haven't added one.
export function Avatar({ id, name, hasPhoto, size = 28 }: { id?: string | null; name?: string | null; hasPhoto?: boolean | number | null; size?: number }) {
  const initials = (name || "?").split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  const style = { width: size, height: size, borderRadius: "50%", flex: "none" as const, objectFit: "cover" as const };
  if (id && hasPhoto) return <img src={`/api/users/${id}/photo`} alt={name ?? ""} style={style} />;
  return (
    <span aria-hidden style={{ ...style, display: "inline-grid", placeItems: "center", background: "var(--accent-soft)", color: "var(--accent-ink)", fontSize: Math.round(size * 0.4), fontWeight: 600 }}>
      {initials}
    </span>
  );
}
